import { EventType, SleepStage } from '@/common/types';
import { ARTIFACT_COLOR_KEY, baseEventColor } from '../signal-graph/signal-graph.d3';
import type { EventCategorySpec, SleepStageRow } from './event-settings.types';

export const DEFAULT_EVENT_COLOR = '#999999';

export const EVENT_CATEGORY_SPECS: EventCategorySpec[] = [
  {
    id: 'apnea',
    labelKey: 'apnea',
    collapsible: false,
    rows: [
      {
        id: 'apnea-obstructive',
        labelKey: EventType.APNEA_OBSTRUCTIVE,
        members: [EventType.APNEA_OBSTRUCTIVE, EventType.APNEA_OBSTRUCTIVE_RESP_FLOW],
        defaultShortcut: 'o'
      },
      {
        id: 'apnea-central',
        labelKey: EventType.APNEA_CENTRAL,
        members: [EventType.APNEA_CENTRAL, EventType.APNEA_CENTRAL_RESP_FLOW],
        defaultShortcut: 'c'
      },
      {
        id: 'apnea-mixed',
        labelKey: EventType.APNEA_MIXED,
        members: [EventType.APNEA_MIXED, EventType.APNEA_MIXED_RESP_FLOW],
        defaultShortcut: 'm'
      }
    ]
  },
  {
    id: 'hypopnea',
    labelKey: 'hypopnea',
    collapsible: false,
    rows: [
      {
        id: 'hypopnea-obstructive',
        labelKey: EventType.HYPOPNEA_OBSTRUCTIVE,
        members: [EventType.HYPOPNEA_OBSTRUCTIVE, EventType.HYPOPNEA_OBSTRUCTIVE_RESP_FLOW],
        defaultShortcut: 'h'
      },
      {
        id: 'hypopnea-central',
        labelKey: EventType.HYPOPNEA_CENTRAL,
        members: [EventType.HYPOPNEA_CENTRAL, EventType.HYPOPNEA_CENTRAL_RESP_FLOW],
        defaultShortcut: 'g'
      }
    ]
  },
  {
    id: 'arrhythmia',
    labelKey: EventType.ARRHYTHMIA,
    collapsible: true,
    rows: [
      EventType.ATRIAL_FIBRILLATION,
      EventType.PAC,
      EventType.PVC,
      EventType.AV_BLOCK,
      EventType.PACEMAKER_RHYTHM,
      EventType.OTHER_ARRHYTHMIA,
      EventType.ASYSTOLE,
      EventType.NARROW_COMPLEX_TACHYCARDIA,
      EventType.WIDE_COMPLEX_TACHYCARDIA
    ].map((type) => ({ id: type, labelKey: type, members: [type] }))
  },
  {
    id: 'bruxism',
    labelKey: 'bruxism',
    collapsible: true,
    rows: [
      {
        id: 'bruxism-phasic',
        labelKey: EventType.BRUXISM_PHASIC_L,
        members: [EventType.BRUXISM_PHASIC_L, EventType.BRUXISM_PHASIC_R]
      },
      {
        id: 'bruxism-tonic',
        labelKey: EventType.BRUXISM_TONIC_L,
        members: [EventType.BRUXISM_TONIC_L, EventType.BRUXISM_TONIC_R]
      },
      {
        id: 'bruxism-mixed',
        labelKey: EventType.BRUXISM_MIX_L,
        members: [EventType.BRUXISM_MIX_L, EventType.BRUXISM_MIX_R]
      }
    ]
  },
  {
    id: 'other',
    collapsible: false,
    rows: [
      {
        id: 'rera',
        labelKey: EventType.RERA,
        members: [EventType.RERA, EventType.RERA_RESP_FLOW],
        defaultShortcut: 'u'
      },
      {
        id: 'desaturation',
        labelKey: EventType.DESATURATION,
        members: [EventType.DESATURATION],
        defaultShortcut: 'd'
      },
      {
        id: 'cheyne-stokes',
        labelKey: EventType.CHEYNE_STOKES_BREATHING,
        members: [EventType.CHEYNE_STOKES_BREATHING],
        defaultShortcut: 'z'
      },
      {
        id: 'arousal',
        labelKey: EventType.AROUSAL_FRONTAL_L,
        members: [EventType.AROUSAL_FRONTAL_L, EventType.AROUSAL_FRONTAL_R],
        defaultShortcut: 'a'
      },
      {
        id: 'snoring',
        labelKey: EventType.SNORING,
        members: [EventType.SNORING],
        defaultShortcut: 's'
      },
      {
        id: 'leg-movement',
        labelKey: EventType.LEG_MOVEMENT,
        members: [EventType.LEG_MOVEMENT],
        defaultShortcut: 'l'
      },
      {
        id: 'periodic-leg-movement',
        labelKey: EventType.PERIODIC_LEG_MOVEMENT,
        members: [EventType.PERIODIC_LEG_MOVEMENT],
        defaultShortcut: 'p'
      },
      {
        id: 'tachycardia',
        labelKey: EventType.TACHYCARDIA,
        members: [EventType.TACHYCARDIA],
        defaultShortcut: 't'
      },
      {
        id: 'bradycardia',
        labelKey: EventType.BRADYCARDIA,
        members: [EventType.BRADYCARDIA],
        defaultShortcut: 'b'
      },
      {
        id: ARTIFACT_COLOR_KEY,
        labelKey: 'artifact',
        members: [],
        isArtifactRow: true,
        infoLabelKey: 'allSignals',
        defaultShortcut: 'x'
      }
    ]
  }
];

const SLEEP_STAGE_SHORTCUTS: Partial<Record<SleepStage, string>> = {
  [SleepStage.N1]: '1',
  [SleepStage.N2]: '2',
  [SleepStage.N3]: '3',
  [SleepStage.REM]: '5',
  [SleepStage.Wake]: '0',
  [SleepStage.Unstaged]: ' '
};

export const SLEEP_STAGE_ROWS: SleepStageRow[] = Object.values(SleepStage).map((stage) => ({
  stage,
  labelKey: stage.toLowerCase(),
  color: baseEventColor[stage],
  shortcut: SLEEP_STAGE_SHORTCUTS[stage] === ' ' ? 'Space' : (SLEEP_STAGE_SHORTCUTS[stage] ?? '')
}));

export const RESERVED_SHORTCUT_KEYS = new Set<string>([
  ...(Object.values(SLEEP_STAGE_SHORTCUTS) as string[]),
  'Delete',
  'Backspace'
]);
